import {
  addWeeks,
  eachDayOfInterval,
  format as dateFnsFormat,
  isSameDay,
  lastDayOfWeek,
  parseISO,
  startOfWeek,
} from 'date-fns';
import { lt as locale } from 'date-fns/locale';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock_gr.svg';
import Form from '../../../components/shared/Form/Form';
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import InlineLoader from '@/components/shared/InlineLoader/InlineLoader';
import { Button } from '@/components/ui/button';
import ServiceQueueUserInformationDialog from '@/v2/pages/ServicesPage/Components/ServiceQueueUserInformationDialog';
import { useUserState } from '@/components/context/UserContext';

const Calendar = ({
  filter,
  dataRequest,
  onChange,
  selected,
  error,
  refresh,
  psdfService,
  hasNearestPsdfDate,
  defaultData = new Date(),
  widget,
  extraPrice,
  servicePrice,
  refreshOfFilter,
  isHalfMrt,
  workplaceId,
  service,
  psdfPrice,
  hasSlots,
  visitPaymentTypeFilter,
  stopLoading,
  showQueue,
}) => {
  const { t } = useTranslation();
  const userState = useUserState();
  const format = (date, formatStr = 'PP') =>
    dateFnsFormat(date, formatStr, { locale });

  const initialDay = filter ? new Date(filter) : new Date();
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(initialDay);
  const [currentDay, setCurrentDay] = useState();
  const [timeSlots, setTimeSlots] = useState([]);
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [loadingSlotId, setLoadingSlotId] = useState(null);
  const [addToQueueDialogOpen, setAddToQueueDialogOpen] = useState(false);

  const weekOptions = { weekStartsOn: 1 };
  const weekStart = startOfWeek(day, weekOptions);
  const endOfWeek = lastDayOfWeek(day, weekOptions);
  const weekStartDate = format(weekStart, 'yyyy-MM-dd');
  const endOfWeekDate = format(endOfWeek, 'yyyy-MM-dd');
  const weekDays = eachDayOfInterval({ start: weekStart, end: endOfWeek });

  useEffect(() => {
    if (!stopLoading) {
      setLoadingSlotId(null);
    }
  }, [stopLoading]);

   useEffect(() => {
    if (!hasSlots) {
      setLoading(false);
      return;
    }
    if (!filter) return;
    setLoading(true);
    dataRequest(
      weekStartDate,
      endOfWeekDate,
      'all',
      service.workplaceId,
      service.serviceId,
    )
      .then((data) => {
        if (data) {
          if (visitPaymentTypeFilter == 2) {
            data = data.filter((x) => !x.isPaid);
          }
          setTimeSlots(data);
          setCurrentDay(day);
        }
        setLoading(false);
      })
      .catch(() => {});
  }, [day]);

  const getTimeSlots = useCallback(() => {
    const slots = [];

    weekDays.forEach((weekDay) => {
      if (Array.isArray(timeSlots)) {
        let daySlots = timeSlots.filter(
          (time) =>
            isSameDay(parseISO(time.startDate), weekDay) &&
            (time.statusName === 'Available' ||
              (time.duplicatedTimeSlotId !== null
                ? selected === time.duplicatedTimeSlotId
                : selected === time.timeSlotId)),
        );

        if (widget) {
          const filteredSlots = daySlots.filter(
            (ts) =>
              ts.statusName === 'Available' &&
              !ts.isReserved &&
              ts.visitStatus === null,
          );
          daySlots = filteredSlots.slice(0, 3);
        }

        slots.push(daySlots);
      }
    });

    const maxLength = slots.reduce(
      (r, s) => (r.length > s.length ? r : s),
      [],
    ).length;

    return slots.map((slot) => [
      ...slot,
      ...new Array(maxLength - slot.length).fill(0),
    ]);
  }, [selected, timeSlots, weekDays]);

  const timeSlotTime = (timeSlotDate) => {
    const moment = require('moment-timezone');
    return moment(timeSlotDate).tz('Europe/Vilnius').format('HH:mm');
  };

  function changeWeek(amount) {
    const newDay = addWeeks(day, amount);
    setLoading(true);
    setDay(newDay);
    setCurrentDay(newDay);
  }

  function showBackButton() {
    return new Date() < new Date(weekStartDate);
  }

  const isEmpty = !getTimeSlots().filter((arr) => !!arr.length).length;

  return (
    <>
      <div className="bg-[#ffffff] rounded-2xl p-0 md:p-5 select-none">
        <div className="flex items-center justify-center py-6">
          {showBackButton() && !widget && (
            <div
              className="h-7 w-7 rounded bg-[#F9FCFF] flex items-center justify-center cursor-pointer hover:opacity-60 mr-3"
              onClick={() => changeWeek(-1)}
            >
              <ChevronLeft className="text-[#1F4B81]" />
            </div>
          )}

          <div className="text-xl font-extrabold text-center min-w-0 sm:min-w-[340px]">
            <span className="sm:hidden">
              <span className="whitespace-nowrap">
                {format(weekStart, 'yyyy-MM-dd')}
              </span>
              {' – '}
              <span className="whitespace-nowrap">
                {format(endOfWeek, 'yyyy-MM-dd')}
              </span>
            </span>

            <span className="hidden sm:inline">
              <span className="whitespace-nowrap">
                {format(weekStart, 'yyyy MMMM d')}
              </span>
              {' – '}
              <span className="whitespace-nowrap">
                {`${format(endOfWeek, 'MMMM d')} d.`}
              </span>
            </span>
          </div>

          {!widget && (
            <div
              className="h-7 w-7 rounded bg-[#F9FCFF] flex items-center justify-center cursor-pointer hover:opacity-60 ml-3"
              onClick={() => changeWeek(1)}
            >
              <ChevronRight className="text-[#1F4B81]" />
            </div>
          )}
        </div>

        <div className="rounded-md bg-white">
          <div className="grid grid-cols-7 gap-2 pb-2 text-center h-13 ">
            {weekDays.map((day, i) => (
              <div
                key={i}
                className="bg-[#F9FCFF] text-sm md:text-md text-[#1F4B81] font-semibold flex items-center justify-center h-13 rounded"
              >
                {format(day, 'ccc. MM-dd').replace(/^./, (c) =>
                  c.toUpperCase(),
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {loading ? (
              <div className="col-span-7 h-[320px] flex justify-center items-center">
                <InlineLoader light={true} />
              </div>
            ) : isEmpty ? (
              <div className="col-span-7 h-[320px] flex flex-col justify-center items-center text-center text-gray-400 text-base max-w-xs mx-auto px-4">
                <div>
                  <ClockIcon className="mx-auto mb-3" />
                  <div>{t('validation.calendarNoAvailableTime')}</div>
                </div>

                {showQueue && (
                  <Button
                    type="button"
                    onClick={() => setAddToQueueDialogOpen(true)}
                    className="px-6 py-2 font-medium rounded-4xl transition mt-3"
                    variant={'default'}
                  >
                    Registruotis į eilę
                  </Button>
                )}
              </div>
            ) : (
              getTimeSlots().map((column, i) => (
                <div key={i} className="flex flex-col">
                  {column
                    .slice(0, showAllSlots ? undefined : 5)
                    .map((item, ii) => (
                      <div key={ii} className="p-1 h-13">
                        {item ? (
                          <div
                            onClick={async () => {
                              if (
                                !(
                                  item.isReserved ||
                                  (item.visitStatus &&
                                    Object.keys(item.visitStatus).length > 0) ||
                                  (item.isPaid &&
                                    (servicePrice ?? 0) +
                                      item.additionalServicesPrice ===
                                      0) ||
                                  (item.isExtra &&
                                    (extraPrice ?? 0) +
                                      item.additionalServicesPrice ===
                                      0)
                                )
                              ) {
                                const slotId =
                                  item.duplicatedTimeSlotId ?? item.timeSlotId;
                                setLoadingSlotId(slotId);
                                try {
                                  await onChange(item);
                                } finally {
                                }
                              }
                            }}
                            className={`flex items-center justify-center text-xs md:text-sm rounded cursor-pointer h-full px-1
                              ${
                                item.isReserved ||
                                item.visitStatus !== null ||
                                (item.isPaid &&
                                  !(
                                    (servicePrice ?? 0) +
                                    item.additionalServicesPrice
                                  )) ||
                                (item.isExtra &&
                                  !(
                                    (extraPrice ?? 0) +
                                    item.additionalServicesPrice
                                  ))
                                  ? 'bg-gray-100 text-gray-400 cursor-default'
                                  : item.isExtra
                                    ? 'bg-[#F2F0FF] text-[#525252] border border-[#7F67DE]'
                                    : item.isPaid
                                      ? 'bg-[#EEF4FF] text-[#525252] border border-[#5180CA]'
                                      : 'bg-[#F8FFF8] text-[#525252] border border-[#72BE77]'
                              }
                              ${
                                selected ===
                                (item.duplicatedTimeSlotId ?? item.timeSlotId)
                                  ? 'font-bold border-2 border-black'
                                  : 'border-1'
                              }
                            `}
                          >
                            {loadingSlotId ===
                            (item.duplicatedTimeSlotId ?? item.timeSlotId) ? (
                              <InlineLoader light={true} />
                            ) : (
                              timeSlotTime(item.startDate)
                            )}
                          </div>
                        ) : (
                          <div className="bg-[#F8F8F8] rounded h-full" />
                        )}
                      </div>
                    ))}
                </div>
              ))
            )}
          </div>

          {!loading && !isEmpty && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="secondary"
                onClick={() => setShowAllSlots(!showAllSlots)}
                className="text-white font-medium text-sm hover:underline flex items-center"
              >
                <span className="mr-1">
                  {showAllSlots
                    ? 'Rodyti mažiau laikų'
                    : 'Rodyti daugiau laikų'}
                </span>
                {showAllSlots ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </div>
          )}

          <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-[#1F4B81]">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#EEF4FF] border border-[#5180CA]" />
              <span>Mokamas laikas</span>
            </div>

            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#F8FFF8] border border-[#72BE77]" />
              <span>PSDF kompensuojamas laikas</span>
            </div>

            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#F2F0FF] border border-[#7F67DE]" />
              <span>Papildomas mokamas laikas</span>
            </div>

            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
              <span>Užimtas laikas</span>
            </div>
          </div>
        </div>

        {error && <Form.Error>{error}</Form.Error>}
      </div>

      <ServiceQueueUserInformationDialog
        open={addToQueueDialogOpen}
        onOpenChange={setAddToQueueDialogOpen}
        isLoggedIn={!!userState?.email}
        initialPhoneNumber={userState?.phoneNumber || ''}
        initialEmail={userState?.email || ''}
        serviceId={service?.serviceId}
        workplaceId={service?.workplaceId}
        onAccept={(data) => {
          // logged in -> data === true
          // not logged in -> data = { phoneNumber, email }
        }}
      />
    </>
  );
};

Calendar.propTypes = {
  dataRequest: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

export default Calendar;
